import { ActivatedRouteSnapshot, CanActivateChildFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { SessionService } from './session.service';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { UsersApiService } from './users-api.service';

/** Protects the global /accounts route — ROOT_ADMIN only. */
export const isAccountSelectedGuard = () => {
    const router = inject(Router);
    const sessionService = inject(SessionService);

    return sessionService.assumedRole$.pipe(
        take(1),
        map(role => role === 'ROOT_ADMIN' ? true : router.createUrlTree(['/']))
    );
};

const ACCOUNT_ROLES = ['ACCOUNT_ADMIN', 'ROOT_ADMIN'];

/**
 * Guard for account-scoped routes (/account/:account_id/...).
 * Allows only ACCOUNT_ADMIN and ROOT_ADMIN.
 * Restores account context from the URL for shared links and page refreshes.
 */
export const accountContextGuard: CanActivateChildFn = (childRoute: ActivatedRouteSnapshot) => {
    const accountId = childRoute.pathFromRoot
        .map(r => r.params['account_id'])
        .find(Boolean) as string | undefined;

    const router = inject(Router);

    const invalidId = !accountId || accountId === 'null' || accountId === 'undefined';
    if (invalidId) return router.createUrlTree(['/not-found']);

    const sessionService = inject(SessionService);
    const usersApi = inject(UsersApiService);

    return sessionService.assumedRole$.pipe(
        take(1),
        switchMap(role => {
            if (!ACCOUNT_ROLES.includes(role)) return of(router.createUrlTree(['/']));

            // Fast path: session already has the correct account.
            if (sessionService.accountOrg$.getValue().account?.id === accountId) {
                return of(true);
            }

            // Restore account context from URL (shared link / page refresh).
            return usersApi.getAccounts$().pipe(
                take(1),
                switchMap(accounts => {
                    const account = accounts.find(a => a.account_id === accountId);
                    if (!account) return of(router.createUrlTree(['/not-found']));
                    return sessionService.setAccountOrg({ id: account.account_id, name: account.name }).pipe(map(() => true));
                }),
                catchError(() => of(router.createUrlTree(['/not-found']))),
            );
        }),
    );
};
